import React from 'react';
import { styled } from '@linaria/react';
import { animated } from 'react-spring';
import { X as XIcon, HelpCircle } from 'lucide-react';

import useBoop from '@/hooks/use-boop';

import Spacer from '@/components/Spacer';
import VisuallyHidden from '@/components/VisuallyHidden';

import PreBoop from './PreBoop';
import BoopV1 from './BoopV1';
import BoopV2 from './BoopV2';
import BoopV3 from './BoopV3';
import BoopV4 from './BoopV4';

const BOOPS = {
  PreBoop,
  BoopV1,
  BoopV2,
  BoopV3,
  BoopV4,
};

interface Props {
  boop?: keyof typeof BOOPS;
  transforms?: [
    {
      rotation: number;
    },
    {
      rotation: number;
    },
  ];
}

function Demo({
  boop,
  transforms = [{ rotation: 35 }, { rotation: 20 }],
}: Props) {
  const [boop1Style, boop1Trigger] = useBoop(transforms[0]);
  const [boop2Style, boop2Trigger] = useBoop(transforms[1]);

  if (boop) {
    const Boop = BOOPS[boop];
    return (
      <Row>
        <Boop {...transforms[0]}>
          <Button>
            <HelpCircle size={48} />
            <VisuallyHidden>
              Help icon (Question mark in a circle)
            </VisuallyHidden>
          </Button>
        </Boop>
        <Spacer size={32} />
        <Boop {...transforms[1]}>
          <Button>
            <XIcon size={48} />
            <VisuallyHidden>Close icon (X symbol)</VisuallyHidden>
          </Button>
        </Boop>
      </Row>
    );
  }

  return (
    <Row>
      <Button
        style={boop1Style}
        onClick={boop1Trigger}
        onMouseEnter={boop1Trigger}
      >
        <HelpCircle size={48} />
        <VisuallyHidden>
          Help icon (Question mark in a circle)
        </VisuallyHidden>
      </Button>
      <Spacer size={32} />
      <Button
        style={boop2Style}
        onClick={boop2Trigger}
        onMouseEnter={boop2Trigger}
      >
        <XIcon size={48} />
        <VisuallyHidden>Close icon (X symbol)</VisuallyHidden>
      </Button>
    </Row>
  );
}

const Row = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 48px;
  margin-top: 16px;
`;

const Button = styled(animated.button)`
  opacity: 0.75;
  color: var(--color-text);
  will-change: opacity;

  svg {
    display: block !important;
  }

  &:hover {
    opacity: 1;
  }
`;

export default Demo;
